import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import {
  StandardTable,
  StandardTableColumn,
} from "../../common/StandardTable/StandardTable";

/** A change to one row's business charge, in the shape the caller sends up. */
export interface ChargeChange {
  id: string;
  charge: number;
}

interface BusinessChargeEditModalProps<T> {
  opened: boolean;
  onClose: () => void;
  title: string;
  rows: T[];
  getRowKey: (row: T) => string;
  getName: (row: T) => string;
  /** Current (saved) charge for a row; drives the initial input value. */
  getCurrentCharge: (row: T) => number;
  /** Read-only columns shown for context (e.g. price), before the charge input. */
  contextColumns?: StandardTableColumn<T>[];
  /** Persist the changed rows; the parent refreshes context state afterward. */
  onSave: (changes: ChargeChange[]) => Promise<void>;
  /** Minimum table width (drives horizontal scroll on narrow screens). */
  minWidth?: number;
}

const coerce = (v: number | string): number =>
  v === "" || v === null || v === undefined ? 0 : Number(v);

/**
 * Bulk-edit modal for business charges, modeled on the Staff Tiers editor: one
 * editable table, dirty-tracked against the values at open, saved in a single
 * bulk call. There is no delete — a blank input is 0. The table scrolls
 * horizontally on narrow screens.
 */
function BusinessChargeEditModal<T>({
  opened,
  onClose,
  title,
  rows,
  getRowKey,
  getName,
  getCurrentCharge,
  contextColumns = [],
  onSave,
  minWidth = 520,
}: BusinessChargeEditModalProps<T>) {
  const [edits, setEdits] = useState<Record<string, number | string>>({});
  const initialRef = useRef<string>("{}");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the latest rows + accessors in refs so the seed effect can depend only
  // on `opened` (the prop functions are fresh arrows on each render).
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const getRowKeyRef = useRef(getRowKey);
  getRowKeyRef.current = getRowKey;
  const getCurrentChargeRef = useRef(getCurrentCharge);
  getCurrentChargeRef.current = getCurrentCharge;

  // Re-seed the inputs from the saved values each time the modal opens.
  useEffect(() => {
    if (!opened) return;
    const initial: Record<string, number | string> = {};
    for (const row of rowsRef.current) {
      initial[getRowKeyRef.current(row)] = getCurrentChargeRef.current(row);
    }
    setEdits(initial);
    initialRef.current = JSON.stringify(initial);
    setError(null);
  }, [opened]);

  const isDirty = JSON.stringify(edits) !== initialRef.current;

  const setCharge = (id: string, value: number | string): void =>
    setEdits((prev) => ({ ...prev, [id]: value }));

  const chargeColumn: StandardTableColumn<T> = {
    key: "__charge",
    label: "Business charge",
    align: "right",
    render: (row) => {
      const id = getRowKey(row);
      return (
        <NumberInput
          aria-label={`Business charge for ${getName(row)}`}
          value={edits[id] ?? getCurrentCharge(row)}
          onChange={(v) => setCharge(id, v)}
          min={0}
          prefix="$"
          decimalScale={2}
          hideControls
          size="xs"
          w={110}
          disabled={saving}
        />
      );
    },
  };

  const nameColumn: StandardTableColumn<T> = {
    key: "__name",
    label: "Name",
    render: (row) => (
      <Text size="sm" fw={500}>
        {getName(row)}
      </Text>
    ),
  };

  const columns = [nameColumn, ...contextColumns, chargeColumn];

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const changes: ChargeChange[] = [];
      for (const row of rows) {
        const id = getRowKey(row);
        const charge = coerce(edits[id]);
        if (charge !== getCurrentCharge(row)) changes.push({ id, charge });
      }
      if (changes.length > 0) {
        await onSave(changes);
      }
      onClose();
    } catch (e) {
      console.error("Error saving business charges:", e);
      setError("Couldn't save the business charges. Check the values.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      centered
      size="xxl"
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        },
      }}
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <StandardTable<T>
          columns={columns}
          data={rows}
          getRowKey={getRowKey}
          minWidth={minWidth}
          maxHeight="60dvh"
          stickyHeader
          ariaLabel={title}
          loading={saving}
          emptyState={
            <Text c="dimmed" size="sm">
              Nothing to configure yet.
            </Text>
          }
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSave}
            loading={saving}
            disabled={!isDirty}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default BusinessChargeEditModal;
